import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoadingIndicationComponent } from '@/app/components/loading-indication/loading-indication.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '@/service/user.service';
import {
  CompanyInvitation,
  CompanyUser,
  CompanyInvitationStatus,
} from '@/model/company';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CompanyEditDialogComponent } from '@/app/company-management/company-edit-dialog/company-edit-dialog.component';

@Component({
  selector: 'app-company-management',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatDividerModule,
    MatTableModule,
    MatSortModule,
    ClipboardModule,
    MatMenuModule,
    MatSnackBarModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    LoadingIndicationComponent,
  ],
  templateUrl: './company-management.component.html',
  styleUrl: './company-management.component.scss',
  standalone: true,
})
export class CompanyManagementComponent implements OnInit, AfterViewInit {
  emailForm: FormGroup;
  invitations: CompanyUser[] = [];
  CompanyInvitationStatus = CompanyInvitationStatus;

  selectedStatus: CompanyInvitationStatus | null = null;
  searchTerm = '';

  loadingState = {
    loading: false,
    sending: false,
  };

  get statusValues(): CompanyInvitationStatus[] {
    return Object.values(CompanyInvitationStatus);
  }

  constructor(
    private fb: FormBuilder,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private userService: UserService,
    private dialog: MatDialog,
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    // Configure a combined filter (status + searchTerm)
    this.dataSource.filterPredicate = (data: CompanyUser) => {
      const matchesStatus =
        !this.selectedStatus || data.status === this.selectedStatus;
      const query = (this.searchTerm || '').trim().toLowerCase();

      if (!query) {
        return matchesStatus;
      }

      const fields = [
        data.companyName,
        data.email,
        data.contactPersonName,
        data.website,
        data.phone,
      ]
        .filter((v): v is string => typeof v === 'string' && v.length > 0)
        .map((v) => v.toLowerCase());

      const matchesQuery = fields.some((f) => f.includes(query));
      return matchesStatus && matchesQuery;
    };

    this.loadInvitations();
  }

  openCreateCompany() {
    const dialogRef = this.dialog.open(CompanyEditDialogComponent, {
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.company) {
        this.userService.adminCreateOrUpdateCompany(result.company).subscribe({
          next: () => {
            this.snackBar.open('Firma gespeichert', 'Schließen', {
              duration: 2000,
            });
            this.loadInvitations();
          },
          error: (error) => {
            console.error('Error saving company:', error);
            this.snackBar.open('Fehler beim Speichern der Firma', 'Schließen', {
              duration: 3000,
            });
          },
        });
      }
    });
  }

  editCompany(invitation: CompanyUser) {
    const dialogRef = this.dialog.open(CompanyEditDialogComponent, {
      // width: '720px',
      data: {
        company: invitation,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.company) {
        this.userService.adminCreateOrUpdateCompany(result.company).subscribe({
          next: () => {
            this.snackBar.open('Firma gespeichert', 'Schließen', {
              duration: 2000,
            });
            this.loadInvitations();
          },
          error: (error) => {
            console.error('Error saving company:', error);
            this.snackBar.open('Fehler beim Speichern der Firma', 'Schließen', {
              duration: 3000,
            });
          },
        });
      }
    });
  }

  loadInvitations() {
    this.loadingState.loading = true;
    this.userService.getAllCompanyInvitations().subscribe({
      next: (invitations) => {
        if (invitations) {
          const mappedInvitations = invitations.map((invitation) =>
            CompanyUser.fromJson(JSON.stringify(invitation)),
          );
          this.invitations = mappedInvitations;
          this.dataSource.data = this.invitations;
        }
        this.loadingState.loading = false;
      },
      error: (error) => {
        console.error('Error loading invitations:', error);
        this.loadingState.loading = false;
      },
    });
  }

  displayedColumns: string[] = ['company', 'email', 'status', 'actions'];
  dataSource = new MatTableDataSource<CompanyUser>(this.invitations);

  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  getStatusText(status: CompanyInvitationStatus): string {
    switch (status) {
      case CompanyInvitationStatus.PENDING:
        return 'Ausstehend';
      case CompanyInvitationStatus.ACCEPTED:
        return 'Akzeptiert';
      case CompanyInvitationStatus.CREATED_NO_EMAIL:
        return 'Manuell erstellt';
      default:
        return 'Unbekannt';
    }
  }

  copyEmail(email: string) {
    this.clipboard.copy(email);
  }

  onSubmit() {
    if (this.emailForm.valid) {
      const email = this.emailForm.get('email')?.value;

      const companyInvitation: CompanyInvitation = {
        email: email,
      };

      this.loadingState.sending = true;
      this.userService.sendCompanyInvitation(companyInvitation).subscribe({
        next: (invitation) => {
          console.log(invitation);
          this.loadInvitations();
          this.emailForm.reset();
          this.snackBar.open('Einladung erfolgreich versendet', 'Schließen', {
            duration: 3000,
          });
          this.loadingState.sending = false;
        },
        error: (error) => {
          console.error('Error sending invitation:', error);
          this.snackBar.open(
            `Fehler beim Senden der Einladung: ${error}`,
            'Schließen',
            {
              duration: 3000,
            },
          );
          this.loadingState.sending = false;
        },
      });
    }
  }

  resendInvitation(invitation: CompanyInvitation) {
    console.log('Einladung erneut senden:', invitation);
    this.userService.resendCompanyInvitation(invitation).subscribe({
      next: (invitation) => {
        console.log(invitation);
        this.loadInvitations();
      },
      error: (error) => {
        console.error('Error sending invitation:', error);
      },
    });
  }

  sendInvitation(invitation: CompanyInvitation) {
    console.log('Einladung senden:', invitation);
    this.userService
      .sendCompanyInvitation({ email: invitation.email })
      .subscribe({
        next: (resp) => {
          console.log(resp);
          this.snackBar.open('Einladung erfolgreich versendet', 'Schließen', {
            duration: 3000,
          });
          this.loadInvitations();
        },
        error: (error) => {
          console.error('Error sending invitation:', error);
          this.snackBar.open('Fehler beim Senden der Einladung', 'Schließen', {
            duration: 3000,
          });
        },
      });
  }

  deleteInvitation(invitation: CompanyInvitation) {
    console.log('Löschen:', invitation);
    this.userService.deleteCompanyInvitation(invitation).subscribe({
      next: () => {
        this.loadInvitations();
      },
      error: (error) => {
        console.error('Error deleting invitation:', error);
      },
    });
  }

  onStatusFilterChange(status: CompanyInvitationStatus) {
    this.selectedStatus = this.selectedStatus === status ? null : status;
    // Trigger filtering with the combined predicate
    this.dataSource.filter = String(Date.now());
  }

  onSearchChange(term: string) {
    this.searchTerm = term;
    // Trigger filtering with the combined predicate
    this.dataSource.filter = String(Date.now());
  }
}
